import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  InputSignal,
  OutputEmitterRef,
  Signal,
  TemplateRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  output,
  viewChild,
  EffectCleanupRegisterFn
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { PipesModule } from '../../pipes/pipes.module';
import { IconButtonComponent } from '../icon-button/icon-button.component';
import { IconComponent } from '../icon/icon.component';
import { IdService } from '../../services/id.service';
import { FocusTrapperFactory } from '../focus/focus-trapper-factory';

import type { FocusTrapper } from '../focus/focus-trapper-factory';

@Component({
  imports: [
    CommonModule,
    PipesModule,
    IconButtonComponent,
    IconComponent
  ],
  selector: 'td-wb-bottom-modal',
  templateUrl: 'bottom-modal.component.html',
  styleUrls: ['bottom-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BottomModalComponent {
  private static readonly TITLE_ID_PREFIX: string = 'td-wb-bottom-modal-title-';

  readonly modalOpened: InputSignal<boolean> = input<boolean>(false);
  readonly showHeader: InputSignal<boolean> = input<boolean>(true);
  readonly centerHeader: InputSignal<boolean> = input<boolean>(false);
  readonly fullScreen: InputSignal<boolean> = input<boolean>(false);
  readonly padding: InputSignal<boolean> = input<boolean>(true);
  readonly focusTrap: InputSignal<boolean> = input<boolean>(true);
  readonly hasBackdrop: InputSignal<boolean> = input<boolean>(true);
  readonly closeOnBackdropClick: InputSignal<boolean> = input<boolean>(true);

  readonly closeEvent: OutputEmitterRef<void> = output<void>({ alias: 'close' });
  readonly backdropClicked: OutputEmitterRef<void> = output<void>();
  readonly onToggle: OutputEmitterRef<boolean> = output<boolean>();

  readonly modalTemplate: Signal<TemplateRef<unknown> | undefined> = viewChild<TemplateRef<unknown>>('modalTemplate');

  private readonly overlay: Overlay = inject(Overlay);
  private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
  private readonly focusTrapperFactory: FocusTrapperFactory = inject(FocusTrapperFactory);
  private readonly idService: IdService = inject(IdService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly titleId: string = this.idService.generateNamespaceSequentialId(BottomModalComponent.TITLE_ID_PREFIX);
  readonly fullScreenClass: Signal<string> = computed(() => this.fullScreen() ? 'td-wb-bottom-modal--full-screen' : '');
  readonly noPadding: Signal<boolean> = computed(() => !this.padding());

  private overlayRef: OverlayRef | null = null;
  private focusTrapper: FocusTrapper | null = null;
  private isDestroyed: boolean = false;
  private closeOverlayTimeout: ReturnType<typeof setTimeout> | null = null;
  private closeOverlayAbortController: AbortController | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.isDestroyed = true;
      this.cancelPendingClose();
    });

    effect((onCleanup: EffectCleanupRegisterFn) => {
      const isOpened = this.modalOpened();
      const template = this.modalTemplate();

      if (isOpened && template) {
        this.openOverlay(template);
      } else if (!isOpened) {
        this.closeOverlay();
      }

      onCleanup(() => {
        this.closeOverlay();
      });
    });
  }

  onKeyDown(): void {
    this.close();
  }

  close(): void {
    this.closeEvent.emit();
  }

  private cancelPendingClose(): void {
    if (this.closeOverlayTimeout) {
      clearTimeout(this.closeOverlayTimeout);
      this.closeOverlayTimeout = null;
    }
    if (this.closeOverlayAbortController) {
      this.closeOverlayAbortController.abort();
      this.closeOverlayAbortController = null;
    }
  }

  private openOverlay(template: TemplateRef<unknown>): void {
    if (this.overlayRef) {
      this.cancelPendingClose();
      const container = this.overlayRef.overlayElement.querySelector('.td-wb-bottom-modal__container');
      if (container) {
        container.classList.remove('td-wb-bottom-modal--leave');
        container.classList.add('td-wb-bottom-modal--enter');
      }
      return;
    }

    const positionStrategy = this.overlay.position().global().width('100%').bottom().left();
    const overlayConfig = new OverlayConfig({
      positionStrategy,
      hasBackdrop: this.hasBackdrop(),
      backdropClass: 'td-wb-modal-backdrop'
    });

    this.overlayRef = this.overlay.create(overlayConfig);

    if (this.hasBackdrop()) {
      this.overlayRef.backdropClick().subscribe(() => {
        this.backdropClicked.emit();
        if (this.closeOnBackdropClick()) {
          this.close();
        }
      });
    }

    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.onKeyDown();
      }
    });

    const portal = new TemplatePortal(template, this.viewContainerRef);
    const portalRef = this.overlayRef.attach(portal);
    this.onToggle.emit(true);
    const element = portalRef.rootNodes[0] as HTMLElement;
    element.classList.add('td-wb-bottom-modal--enter');

    if (this.focusTrap()) {
      this.focusTrapper = this.focusTrapperFactory.create(
        element,
        { autoFocus: true, defer: true }
      );
      this.focusTrapper.savePreviouslyFocusedElement();
      this.focusTrapper.autoFocusFirstTabbableElement();
    }
  }

  private closeOverlay(): void {
    this.cancelPendingClose();
    if (this.focusTrapper) {
      this.focusTrapper.returnFocusAfterClose();
      this.focusTrapper.destroy();
      this.focusTrapper = null;
    }

    if (this.overlayRef) {
      const container = this.overlayRef.overlayElement.querySelector('.td-wb-bottom-modal__container');
      if (container) {
        container.classList.remove('td-wb-bottom-modal--enter');
        container.classList.add('td-wb-bottom-modal--leave');

        const onEnd = (): void => {
          this.disposeOverlay();
        };

        this.closeOverlayAbortController = new AbortController();
        container.addEventListener('animationend', onEnd, { once: true, signal: this.closeOverlayAbortController.signal });
        container.addEventListener('transitionend', onEnd, { once: true, signal: this.closeOverlayAbortController.signal });

        this.closeOverlayTimeout = setTimeout(onEnd, 350);
      } else {
        this.disposeOverlay();
      }
    }
  }

  private disposeOverlay(): void {
    this.cancelPendingClose();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;

      if (!this.isDestroyed) {
        this.onToggle.emit(false);
      }
    }
  }
}
